import React, { useRef, useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Code2, Heart, Target, Award, Lightbulb, Code, ChevronLeft, ChevronRight, Briefcase, MapPin, ExternalLink, ChevronDown } from 'lucide-react'
import Slideshow from '../components/Slideshow'
import YouTubeCarousel from '../components/YouTubeCarousel'
import { aboutData } from '../data/about'

const JourneyTimeline = () => {
  const [expandedIndex, setExpandedIndex] = useState(null)
  const [orderedItems, setOrderedItems] = useState([])

  // Sort timeline by year (most recent first)
  const sortedTimeline = [...aboutData.timeline].sort((a, b) => {
    const aStart = parseInt(a.year.split('-')[0])
    const bStart = parseInt(b.year.split('-')[0])
    return bStart - aStart
  })

  // Initialize ordered items
  useEffect(() => {
    setOrderedItems(sortedTimeline.map((item, idx) => ({ item, originalIndex: idx })))
  }, [])

  const toggleItem = (currentIndex) => {
    if (expandedIndex === currentIndex) {
      // Close if clicking the same item
      setExpandedIndex(null)
    } else {
      // Open new item - don't reorder, just expand in place
      setExpandedIndex(currentIndex)
    }
  }

  return (
    <div className="columns-1 md:columns-2 gap-4">
      {orderedItems.length > 0 ? orderedItems.map(({ item, originalIndex }, displayIndex) => {
        const isExpanded = expandedIndex === originalIndex

        return (
          <motion.div
            key={originalIndex}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: displayIndex * 0.05 }}
            className="border border-base-300 rounded-xl bg-base-200/30 hover:bg-base-200/50 hover:border-primary/50 transition-all overflow-hidden break-inside-avoid mb-4"
          >
            {/* Company Header - Always Visible */}
            <button
              onClick={() => toggleItem(originalIndex)}
              className="w-full p-6 flex items-center justify-between hover:bg-base-200/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="badge badge-primary badge-lg font-bold">{item.year}</div>
                <div className="text-lg font-semibold text-base-content">
                  {item.company}
                </div>
              </div>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown className="w-6 h-6 text-primary" />
              </motion.div>
            </button>

            {/* Expanded Content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 border-t border-base-300 pt-6">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      whileHover={{ scale: 1.01, y: -2 }}
                      className="p-6 border border-base-300 rounded-xl bg-base-200/30 hover:bg-base-200/50 hover:border-primary/50 transition-all shadow-sm hover:shadow-lg"
                    >
                      {/* Role Header */}
                      <div className="mb-4 pb-4 border-b border-base-300">
                        <p className="text-xl text-primary font-semibold mb-2">{item.role}</p>
                        {item.location && (
                          <div className="flex items-center gap-1 text-sm text-base-content/60">
                            <MapPin className="w-4 h-4" />
                            <span>{item.location}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Description Bullets */}
                      <ul className="space-y-2 mb-4">
                        {item.description.map((desc, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-base-content/80">
                            <span className="text-primary mt-1.5 flex-shrink-0">•</span>
                            <span className="leading-relaxed">{desc}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {/* Links */}
                      {item.links && item.links.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-4 border-t border-base-300">
                          {item.links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline btn-primary gap-2"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {new URL(link).hostname.replace('www.', '')}
                            </a>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )
      }) : null}
    </div>
  )
}

const SkillsCarousel = () => {
  const scrollRef = useRef(null)
  const animationRef = useRef(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // Auto-scroll animation
  useEffect(() => {
    if (!scrollRef.current || isPaused || isDragging) return

    const scrollElement = scrollRef.current
    const scrollSpeed = 0.5 // pixels per frame (slower = smoother)
    
    const animate = () => {
      if (!isPaused && !isDragging && scrollElement) {
        scrollElement.scrollLeft += scrollSpeed
        
        // Reset to beginning when reaching the duplicate set
        const { scrollLeft, scrollWidth } = scrollElement
        const firstSetWidth = scrollWidth / 2
        
        if (scrollLeft >= firstSetWidth) {
          scrollElement.scrollLeft = scrollLeft - firstSetWidth
        }
        
        animationRef.current = requestAnimationFrame(animate)
      }
    }
    
    animationRef.current = requestAnimationFrame(animate)
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPaused, isDragging])


  // Drag handlers
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return
    setIsDragging(true)
    setIsPaused(true)
    setStartX(e.pageX - scrollRef.current.offsetLeft)
    setScrollLeft(scrollRef.current.scrollLeft)
    scrollRef.current.style.cursor = 'grabbing'
    scrollRef.current.style.userSelect = 'none'
  }

  const handleMouseLeave = () => {
    if (isDragging && scrollRef.current) {
      // Ensure we're in the first set when leaving during drag
      const { scrollLeft, scrollWidth } = scrollRef.current
      const firstSetWidth = scrollWidth / 2
      
      if (scrollLeft >= firstSetWidth) {
        scrollRef.current.scrollLeft = scrollLeft - firstSetWidth
      } else if (scrollLeft < 0) {
        scrollRef.current.scrollLeft = firstSetWidth + scrollLeft
      }
    }
    setIsDragging(false)
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab'
      scrollRef.current.style.userSelect = 'auto'
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    if (scrollRef.current) {
      // Ensure we're in the first set after dragging
      const { scrollLeft, scrollWidth } = scrollRef.current
      const firstSetWidth = scrollWidth / 2
      
      if (scrollLeft >= firstSetWidth) {
        scrollRef.current.scrollLeft = scrollLeft - firstSetWidth
      } else if (scrollLeft < 0) {
        scrollRef.current.scrollLeft = firstSetWidth + scrollLeft
      }
      
      scrollRef.current.style.cursor = 'grab'
      scrollRef.current.style.userSelect = 'auto'
    }
    setTimeout(() => setIsPaused(false), 100)
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollRef.current.offsetLeft
    const walk = (x - startX) * 2 // Scroll speed multiplier
    let newScrollLeft = scrollLeft - walk
    
    // Handle seamless looping
    const { scrollWidth } = scrollRef.current
    const firstSetWidth = scrollWidth / 2
    
    // If scrolled past the duplicate set, loop back to beginning
    if (newScrollLeft >= firstSetWidth) {
      newScrollLeft = newScrollLeft - firstSetWidth
    }
    // If scrolled before the beginning, loop to end
    else if (newScrollLeft < 0) {
      newScrollLeft = firstSetWidth + newScrollLeft
    }
    
    scrollRef.current.scrollLeft = newScrollLeft
  }

  const skillCategories = Object.entries(aboutData.skillCategories).map(([key, category]) => ({
    key,
    ...category
  }))

  const SkillCard = ({ categoryKey, category, isDuplicate = false }) => {
    if (categoryKey === 'devops') return null
    
    if (categoryKey === 'programming') {
      const devopsCategory = aboutData.skillCategories.devops
      return (
        <motion.div
          key={isDuplicate ? `${categoryKey}-dup` : categoryKey}
          whileHover={{ scale: 1.05, y: -5 }}
          className="flex-shrink-0 w-80 p-6 border border-base-300 rounded-xl bg-base-200/30 hover:bg-base-200/50 hover:border-primary/50 transition-all shadow-lg"
        >
          <h3 className="text-base font-bold mb-4 text-primary uppercase tracking-wide flex items-center gap-2">
            <Code className="w-5 h-5" />
            {category.title} / {devopsCategory.title}
          </h3>
          <div className="space-y-2">
            {category.items.map((item, idx) => (
              <div
                key={idx}
                className="text-sm text-base-content/80 leading-relaxed flex items-start gap-2 group"
              >
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span className="group-hover:text-primary transition-colors">{item}</span>
              </div>
            ))}
            {devopsCategory.items.map((item, idx) => (
              <div
                key={`devops-${idx}`}
                className="text-sm text-base-content/80 leading-relaxed flex items-start gap-2 group"
              >
                <span className="text-primary mt-1 flex-shrink-0">•</span>
                <span className="group-hover:text-primary transition-colors">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )
    }
    
    return (
      <motion.div
        key={isDuplicate ? `${categoryKey}-dup` : categoryKey}
        whileHover={{ scale: 1.05, y: -5 }}
        className="flex-shrink-0 w-80 p-6 border border-base-300 rounded-xl bg-base-200/30 hover:bg-base-200/50 hover:border-primary/50 transition-all shadow-lg"
      >
        <h3 className="text-base font-bold mb-4 text-primary uppercase tracking-wide flex items-center gap-2">
          <Code className="w-5 h-5" />
          {category.title}
        </h3>
        <div className="space-y-2">
          {category.items.map((item, idx) => (
            <div
              key={idx}
              className="text-sm text-base-content/80 leading-relaxed flex items-start gap-2 group"
            >
              <span className="text-primary mt-1 flex-shrink-0">•</span>
              <span className="group-hover:text-primary transition-colors">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    )
  }

  return (
    <div className="relative w-full">
      {/* Gradient Overlays - Full width */}
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-base-200/50 via-base-200/50 to-transparent z-20 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-base-200/50 via-base-200/50 to-transparent z-20 pointer-events-none" />
      
      {/* Scrollable Skills Container - Full width */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto scrollbar-hide px-4 py-4 w-full cursor-grab active:cursor-grabbing"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        onMouseEnter={() => !isDragging && setIsPaused(true)}
        onMouseLeave={() => {
          handleMouseLeave()
          if (!isDragging) setIsPaused(false)
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {/* First set */}
        {skillCategories.map((category) => (
          <SkillCard key={category.key} categoryKey={category.key} category={category} />
        ))}
        
        {/* Duplicate set for seamless loop */}
        {skillCategories.map((category) => (
          <SkillCard key={`${category.key}-dup`} categoryKey={category.key} category={category} isDuplicate={true} />
        ))}
      </div>
    </div>
  )
}

const Home = () => {
  return (
    <div className="min-h-screen pt-16">
      {/* Slideshow Section - Top of page */}
      <section className="w-full">
        <Slideshow />
      </section>

      {/* About Section - Replaced Hero */}
      <section className="py-20 scroll-mt-20" id="home">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">
              {aboutData.personal.title}
            </h1>
            <p className="text-2xl md:text-3xl text-base-content/80 mb-8 font-medium">
              {aboutData.personal.subtitle}
            </p>
            <p className="text-lg md:text-xl text-base-content/70 max-w-3xl mx-auto mb-10 leading-relaxed">
              16+ years of experience in software, game, and web development. 
              Led engineering on a $20M valued game. Specializing in C++, Unreal Engine, 
              multiplayer systems, AI integration, and cutting-edge solutions.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4 text-base-content/70 mb-8">
              <a 
                href={`tel:${aboutData.personal.phone}`} 
                className="px-4 py-2 rounded-lg bg-base-200/50 hover:bg-primary/10 hover:text-primary transition-all border border-base-300/50 hover:border-primary/50"
              >
                {aboutData.personal.phone}
              </a>
              <span className="text-base-content/40">•</span>
              <a 
                href={`mailto:${aboutData.personal.email}`} 
                className="px-4 py-2 rounded-lg bg-base-200/50 hover:bg-primary/10 hover:text-primary transition-all border border-base-300/50 hover:border-primary/50"
              >
                {aboutData.personal.email}
              </a>
              <span className="text-base-content/40">•</span>
              <a 
                href={`https://${aboutData.personal.website}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="px-4 py-2 rounded-lg bg-base-200/50 hover:bg-primary/10 hover:text-primary transition-all border border-base-300/50 hover:border-primary/50"
              >
                {aboutData.personal.website}
              </a>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Bio */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="p-8"
            >
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <Code2 className="w-8 h-8 text-primary" />
                Professional Summary
              </h2>
              {aboutData.bio.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-base-content/70 mb-4 leading-relaxed">
                  {paragraph}
                </p>
              ))}
              <div className="mt-6 pt-6 border-t border-base-300">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-primary">16+</div>
                    <div className="text-sm text-base-content/70">Years Experience</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">$20M+</div>
                    <div className="text-sm text-base-content/70">Project Value</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-primary">AAA</div>
                    <div className="text-sm text-base-content/70">Studio Experience</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Values */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {aboutData.values.map((value, index) => {
                const iconMap = {
                  heart: Heart,
                  target: Target,
                  award: Award,
                  lightbulb: Lightbulb,
                }
                const IconComponent = iconMap[value.icon] || Heart
                return (
                  <div key={index} className="p-6 border border-base-300 rounded-lg bg-base-200/30">
                    <div className="flex items-start gap-4">
                      <IconComponent className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                        <p className="text-base-content/70">{value.description}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* YouTube Carousel Section - Project Showcase */}
      <section id="project-showcase" className="py-16 bg-base-200/50 overflow-hidden relative scroll-mt-20">
        <YouTubeCarousel />
      </section>

      {/* Timeline Section - Auto-scrolling Container - Professional Journey */}
      <section id="professional-journey" className="py-20 bg-base-100 scroll-mt-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-4 text-center text-gradient flex items-center justify-center gap-3">
              <Briefcase className="w-10 h-10 text-primary" />
              Professional Journey
            </h2>
            <p className="text-center text-base-content/60 mb-8">16+ years of experience across game development, full-stack engineering, and technical leadership</p>
            
            {/* Auto-scrolling Container */}
            <JourneyTimeline />
          </motion.div>
        </div>
      </section>

      {/* Skills Section - Navigable Sliding Design - Technical Skills */}
      <section id="technical-skills" className="py-16 bg-base-200/50 overflow-hidden relative scroll-mt-20">
        <div className="container mx-auto px-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-2 text-gradient flex items-center justify-center gap-2">
              <Code className="w-10 h-10 text-primary" />
              Technical Skills
            </h2>
            <p className="text-base-content/60">Use arrows to navigate through all skills</p>
          </motion.div>
        </div>

        {/* Sliding Container with Navigation */}
        <SkillsCarousel />
      </section>
    </div>
  )
}

export default Home
